/* eslint-disable */
import { CancelToken, CancelTokenSource } from 'axios';
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useMemo
} from 'react';


import { getCancelTokenSource } from '../../../api/client';
import { getSelectedStore } from '../../../utils/utils';
import { getStoreConfigs, getStoreDetailByNumber } from '../../../api/user';
import { CustomerSkipTraceRequestLevelsType, StoreConfigsResponse, StoreDetail } from '../../../types/types';


export interface StoreState {
  store?: StoreDetail;
  loading: boolean;
  hasApiError: boolean;
  isAddressDoctorEnabled: boolean;
  isRetentionEnabled: boolean;
  referenceContactInterval: number;
  contactWeekLimit: number;
  isExchangeEnabled: boolean;
  exchangePeriod: string;
  isGoogleAddressValidationEnabled: boolean;
  isExchangeFuncEnabled: boolean;
  rewardsEligibilityAmount: number;
  customerRewards: boolean;
  customerVerificationOptimization: boolean;
  isLMDEnabled: boolean;
  enableFieldVisitStrategy: boolean;
  maxReceiptsEmailable: number;
  isCustomerRecordCheckEnabled: boolean;
  customerDataUnmaskingThresholdDays: number;
  customerSkipTraceRequestLevels: CustomerSkipTraceRequestLevelsType;
  isMXAccountManagement: boolean;
  MXAccountMgmtAccess: number;
  customerRecordCheckRequestThresholdDays: number;
  potentialRecordCheck: number[];
  textLanguage: string;
  MXAddressValidation: boolean;
  enableAddressValidation: number | undefined;
}

const NO_LIMIT = 9999;
const NO_LIMIT_STRING = "-1";
const NO_INTERVAL_LIMIT = 0;

export interface StoreDispatchState {
  fetchStoreDetailsByNumber: (
    storeNumber: string,
    cancelToken: CancelTokenSource
  ) => Promise<void | StoreDetail[]>;
}

export const checkIfAddressDoctorEnabled = (
  response: StoreConfigsResponse[]
): boolean => {
  const addressDoctor = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "AddressDoctorEnabledIndicator" &&
      config.paramCategoryName === "AddressDoctorEnabled" &&
      config.paramKeyName === "AddressDoctorEnabled"
    );
  });
  if (addressDoctor && addressDoctor.paramValue === "1") {
    return true;
  }
  return false;
};

export const getLMDEnabled = (response: StoreConfigsResponse[]): boolean => {
  const lmd = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "EnableLMD";
  });
  if (lmd && lmd.paramValue === "1") {
    return true;
  }
  return false;
};

export const shouldCreateFieldVisit = (response: StoreConfigsResponse[]): boolean => {
  const fv = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "EnableFieldVisitStrategy";
  });
  if (fv && fv.paramValue === "1") {
    return true;
  }
  return false;
};
export const getMXAccountManagement = (
  response: StoreConfigsResponse[]
): boolean => {
  const mxAccManage = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "MXAccountManagement";
  });
  if (mxAccManage && mxAccManage.paramValue === "1") {
    return true;
  }
  return false;
};

export const checkIfRetentionEnabled = (
  response: StoreConfigsResponse[]
): boolean => {
  const retentionResponse = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "PaymentAccess" &&
      config.paramCategoryName === "SubSystemAccess" &&
      config.paramKeyName === "CustomerRetention"
    );
  });
  if (retentionResponse && retentionResponse.paramValue === "1") {
    return true;
  }
  return false;
};

export const getAgreementOpenPeriod = (
  response: StoreConfigsResponse[]
): string => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "AgreementExchangePeriod"
  );
  if (exchangeResponse && exchangeResponse.paramValue) {
    return exchangeResponse.paramValue;
  }
  return "";
};
export const checkIfExchangeEnabled = (
  response: StoreConfigsResponse[]
): boolean => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "AgreementExchange"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const getContactWeekLimit = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "CommunicationLimit" &&
      config.paramCategoryName === "AccountManagement" &&
      config.paramKeyName === "CustomerContactLimitPastWeek"
    );
  });
  if (configResp && configResp.paramValue !== NO_LIMIT_STRING) {
    return Number(configResp.paramValue);
  }
  return NO_LIMIT;
};

export const checkIsAddressValidationEnabled = (
  response: StoreConfigsResponse[]
) => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "EnableGoogleAddressValidation"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const checkIsMXAddressValidationEnabled = (
  response: StoreConfigsResponse[]
) => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "MXAddressValidation"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const getReferenceContactInterval = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find((config) => {
    return (
      config.paramGroupName === "CommunicationLimit" &&
      config.paramCategoryName === "AccountManagement" &&
      config.paramKeyName === "ReferenceContactInterval"
    );
  });
  if (configResp && configResp.paramValue !== NO_LIMIT_STRING) {
    return Number(configResp.paramValue);
  }
  return NO_INTERVAL_LIMIT;
};

export const checkIsExchangeFuncEnabled = (
  response: StoreConfigsResponse[]
) => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "RACExchange"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};

export const CustomerRewards = (response: StoreConfigsResponse[]): boolean => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerRewards"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const CustomerVerificationOptimization = (
  response: StoreConfigsResponse[]
): boolean => {
  const exchangeResponse = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerVerificationOptimization"
  );
  if (exchangeResponse && exchangeResponse.paramValue === "1") {
    return true;
  }
  return false;
};
export const RewardsEligibilityAmount = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "RewardsEligibilityAmount";
  });
  if (configResp && configResp.paramValue !== NO_LIMIT_STRING) {
    return Number(configResp.paramValue);
  }
  return NO_INTERVAL_LIMIT;
};

export const getMaxReceiptsEmailable = (
  response: StoreConfigsResponse[]
): number => {
  const maxReceipts = response?.[0]?.configDetails?.find((config) => {
    return config.paramKeyName === "MaxReceiptsEmailable";
  });
  // if maxReceipts is undefined, NaN || 0 will still be 0
  return Number(maxReceipts?.paramValue) || 0;
};

export const CustomerRecordCheckAccess = (
  response: StoreConfigsResponse[]
): boolean => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'EnableCustomerRecordCheckPhase2'
  );
  if (configResp && configResp.paramValue === "1") {
    return true;
  }
  return false;
};

export const CustomerDataUnmaskingThresholdDays = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerDataUnmaskingThresholdDays"
  );
  return Number(configResp?.paramValue) || 0;
};

export const MXAccountManagementAccess = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'MXAccountManagement'
  );
  return Number(configResp?.paramValue) || 0;
};

export const CustomerSkipTraceRequestLevels = (
  response: StoreConfigsResponse[]
): Record<string, number[]> => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === "CustomerSkipTraceRequestLevels"
  );
  return configResp?.paramValue
    ? JSON.parse(configResp.paramValue.replace(/[“”]/g, '"'))
    : { NW: [1, 2, 3, 4, 5] };
};

export const CustomerRecordCheckRequestThresholdDays = (
  response: StoreConfigsResponse[]
): number => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'CustomerRecordCheckRequestThresholdDays'
  );
  return Number(configResp?.paramValue) || 0;
};

export const EnableAddressValidation = (
  response: StoreConfigsResponse[]
): any => {
  const configResp = response?.[0]?.configDetails?.find(
    (config) => config.paramKeyName === 'EnableAddressValidation'
  );
  return Number(configResp?.paramValue) || 0;
};

export interface StoreDispatchState {
  fetchStoreDetailsByNumber: (storeNumber: string, cancelToken: CancelTokenSource) => Promise<void | StoreDetail[]>;
}

// [Helper functions (checkIfRetentionEnabled, getLMDEnabled, etc.) remain the same as provided]

export const StoreStateContext = createContext<StoreState>({} as StoreState);
export const StoreDispatchContext = createContext<StoreDispatchState>({} as StoreDispatchState);

export const StoreProviders = ({ children }: { children: ReactNode }) => {
  const [store, setStore] = useState<StoreDetail>();
  const [loading, setLoading] = useState<boolean>(false);
  const [hasApiError, setHasApiError] = useState(false);
  const [isAddressDoctorEnabled, setIsAddressDoctorEnabled] = useState(false);
  const [isRetentionEnabled, setIsRetentionEnabled] = useState(false);
  const [isExchangeEnabled, setIsExchangeEnabled] = useState(false);
  const [exchangePeriod, setExchangePeriod] = useState<string>("");
  const [contactWeekLimit, setContactWeekLimit] = useState(NO_LIMIT);
  const [isGoogleAddressValidationEnabled, setIsGoogleAddressValidationEnabled] = useState<boolean>(false);
  const [isExchangeFuncEnabled, setIsExchangeFuncEnabled] = useState<boolean>(false);
  const [referenceContactInterval, setReferenceContactInterval] = useState(NO_LIMIT);
  const [rewardsEligibilityAmount, setRewardsEligibilityAmount] = useState(NO_LIMIT);
  const [customerRewards, setCustomerRewards] = useState<boolean>(false);
  const [customerVerificationOptimization, setCustomerVerificationOptimization] = useState<boolean>(false);
  const [isLMDEnabled, setIsLMDEnabled] = useState(false);
  const [enableFieldVisitStrategy, setEnableFieldVisitStrategy] = useState(false);
  const [maxReceiptsEmailable, setMaxReceiptsEmailable] = useState(0);
  const [isCustomerRecordCheckEnabled, setIsCustomerRecordCheckEnabled] = useState(false);
  const [customerDataUnmaskingThresholdDays, setCustomerDataUnmaskingThresholdDays] = useState(0);
  const [customerSkipTraceRequestLevels, setCustomerSkipTraceRequestLevels] = useState<Record<string, number[]>>({});
  const [isMXAccountManagement, setIsMXAccountManagement] = useState(false);
  const [MXAccountMgmtAccess, setMXAccountMgmtAccess] = useState(0);
  const [customerRecordCheckRequestThresholdDays, setCustomerRecordCheckRequestThresholdDays] = useState(0);
  const [potentialRecordCheck, setPotentialRecordCheck] = useState<number[]>([]);
  const [textLanguage, setTextLanguage] = useState<string>("");
  const [MXAddressValidation, setMXAddressValidation] = useState(false);
  const [enableAddressValidation, setEnableAddressValidation] = useState<number | undefined>(undefined);

  const fetchStoreConfig = (selectedStore: string, cancelToken: CancelTokenSource) => {
    const paramKeyName = [
      'AddressDoctorEnabled', 'CustomerRetention', 'CustomerContactLimitPastWeek', 'ReferenceContactInterval',
      'AgreementExchange', 'AgreementExchangePeriod', 'EnableGoogleAddressValidation', 'RACExchange',
      'RewardsEligibilityAmount', 'CustomerRewards', 'Txt2PayCardOnFile', 'CustomerVerificationOptimization',
      'EnableLMD', 'EnableFieldVisitStrategy', 'MaxReceiptsEmailable', 'EnableCustomerRecordCheckPhase2',
      'CustomerDataUnmaskingThresholdDays', 'CustomerSkipTraceRequestLevels', 'MXAccountManagement',
      'CustomerRecordCheckRequestThresholdDays', 'MXAddressValidation', 'EnableAddressValidation'
    ];
    getStoreConfigs({ storeNumbers: [selectedStore], paramKeyNames: paramKeyName }, cancelToken.token)
      .then((response) => {
        setIsRetentionEnabled(checkIfRetentionEnabled(response));
        setIsAddressDoctorEnabled(checkIfAddressDoctorEnabled(response));
        setContactWeekLimit(getContactWeekLimit(response));
        setReferenceContactInterval(getReferenceContactInterval(response));
        setIsExchangeEnabled(checkIfExchangeEnabled(response));
        setExchangePeriod(getAgreementOpenPeriod(response));
        setIsGoogleAddressValidationEnabled(checkIsAddressValidationEnabled(response));
        setMXAddressValidation(checkIsMXAddressValidationEnabled(response));
        setIsExchangeFuncEnabled(checkIsExchangeFuncEnabled(response));
        setRewardsEligibilityAmount(RewardsEligibilityAmount(response));
        setCustomerRewards(CustomerRewards(response));
        setCustomerVerificationOptimization(CustomerVerificationOptimization(response));
        const value = getMXAccountManagement(response);
        setIsMXAccountManagement(value);
        setTextLanguage(value ? 'S' : 'en');
        setIsLMDEnabled(getLMDEnabled(response));
        setEnableFieldVisitStrategy(shouldCreateFieldVisit(response));
        setMaxReceiptsEmailable(getMaxReceiptsEmailable(response));
        setIsCustomerRecordCheckEnabled(CustomerRecordCheckAccess(response));
        setCustomerDataUnmaskingThresholdDays(CustomerDataUnmaskingThresholdDays(response));
        setCustomerSkipTraceRequestLevels(CustomerSkipTraceRequestLevels(response));
        setMXAccountMgmtAccess(MXAccountManagementAccess(response));
        setCustomerRecordCheckRequestThresholdDays(CustomerRecordCheckRequestThresholdDays(response));
        setEnableAddressValidation(EnableAddressValidation(response));
      })
      .catch((error) => {
        setHasApiError(true);
      });
  };

  const fetchStoreDetailsByNumber = async (storeNumber: string, cancelToken: CancelTokenSource) => {
    setHasApiError(false);
    setLoading(true);
    try {
      const response = await getStoreDetailByNumber(storeNumber, cancelToken.token);
      if (response?.response?.length) setStore(response.response[0]);
    } catch (error) {
      setHasApiError(true);
    } finally {
      setLoading(false);
    }
  };

  const selectedStore = useMemo(() => getSelectedStore(), []);
  useEffect(() => {
    const cancelToken = getCancelTokenSource();
    fetchStoreDetailsByNumber(selectedStore, cancelToken);
    fetchStoreConfig(selectedStore, cancelToken);
    return () => cancelToken.cancel();
  }, [selectedStore]);

  return (
    <StoreStateContext.Provider
      value={{
        store,
        loading,
        hasApiError,
        isAddressDoctorEnabled,
        isRetentionEnabled,
        referenceContactInterval,
        contactWeekLimit,
        isExchangeEnabled,
        exchangePeriod,
        isGoogleAddressValidationEnabled,
        isExchangeFuncEnabled,
        rewardsEligibilityAmount,
        customerRewards,
        customerVerificationOptimization,
        isLMDEnabled,
        enableFieldVisitStrategy,
        maxReceiptsEmailable,
        isCustomerRecordCheckEnabled,
        customerDataUnmaskingThresholdDays,
        customerSkipTraceRequestLevels,
        isMXAccountManagement,
        MXAccountMgmtAccess,
        customerRecordCheckRequestThresholdDays,
        potentialRecordCheck,
        textLanguage,
        MXAddressValidation,
        enableAddressValidation,
      }}
    >
      <StoreDispatchContext.Provider value={{ fetchStoreDetailsByNumber }}>
        {children}
      </StoreDispatchContext.Provider>
    </StoreStateContext.Provider>
  );
};

export const useStoreDetails = () => useContext(StoreStateContext);
export const useStoreActions = () => useContext(StoreDispatchContext);